import { useState, useCallback, useEffect, useRef } from 'react';
import type { ProductListing } from '../../types/products';
import { CompactProductCard } from '../products/CompactProductCard';
import { dataLayerPushAsync } from '../../utils/tracking';

const STORAGE_KEY = 'fbt_dismissed_until';

function isDismissed(): boolean {
  try {
    const until = sessionStorage.getItem(STORAGE_KEY);
    return !!until && Date.now() < Number(until);
  } catch {
    return false;
  }
}

interface Props {
  products: ProductListing[];
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
  /** Experimento growth-upsell-carrito: viaja en los eventos de tracking. */
  variant?: string;
  /** 'fbt' (co-compra global) o 'personal' (reposición del propio cliente). */
  source?: 'fbt' | 'personal';
}

const COPY = {
  fbt: {
    title: 'También te puede interesar',
    subtitle: 'Productos que otros clientes compraron junto a los tuyos',
  },
  personal: {
    title: '¿Te falta algo de lo de siempre?',
    subtitle: 'Estos productos los pedís seguido y no están en tu carrito',
  },
} as const;

export function FrequentlyBoughtTogether({ products, onAddToCart, variant = 'control', source = 'fbt' }: Props) {
  const [dismissed, setDismissed] = useState(isDismissed);

  // Impression tracking (routed to PostHog via GTM): upsell_add_to_cart alone
  // has no denominator — without impressions we can't tell acceptance rate.
  const impressionSent = useRef(false);
  useEffect(() => {
    if (impressionSent.current || products.length === 0 || dismissed) return;
    impressionSent.current = true;
    dataLayerPushAsync({
      event: 'upsell_impression',
      upsell_source: source,
      variant,
      placement: 'cart',
      products_count: products.length,
    });
  }, [products.length, dismissed, source, variant]);

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(Date.now() + 30 * 60 * 1000));
    } catch { /* */ }
    setDismissed(true);
  }, []);

  if (products.length === 0 || dismissed) return null;

  return (
    <div className="my-4 border border-green-200 bg-green-50 rounded-xl overflow-hidden">
      <div className="px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <h3 className="text-base font-semibold text-gray-800 m-0 mb-1">
              {COPY[source].title}
            </h3>
            <p className="text-sm text-gray-500 m-0 mb-3">
              {COPY[source].subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
            aria-label="Ocultar sugerencias"
          >
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
          {products.map((product) => (
            <CompactProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
          ))}
        </div>
      </div>
    </div>
  );
}
